import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, Circle, CircleDot, Lock, ListChecks, ChevronRight, Sparkles } from "lucide-react";
import api, { formatErr } from "../lib/api";
import StagePanel from "./StagePanel";
import InfraStagePanel from "./InfraStagePanel";
import { DownloadDPRButton } from "./StageOutputViewer";
import MicButton from "./MicButton";
import { useT } from "../lib/i18n";
import useStageGate from "../lib/useStageGate";

const STATUS_META = {
  not_started: { Icon: Circle,        color: "var(--pf-text-dim)" },
  in_progress: { Icon: CircleDot,     color: "var(--pf-spark)" },
  complete:    { Icon: CheckCircle2,  color: "var(--pf-ok)" },
  approved:    { Icon: CheckCircle2,  color: "var(--pf-accent)" },
  blocked:     { Icon: Lock,          color: "var(--pf-err)" },
};

export default function StageWorkspaceTab({ projectId, onOpenTab, onSendHandover,
                                                                hideRail = false,
                                                                externalActiveCode = null,
                                                                onActiveCodeChange,
                                                                stageExtraTabs = null,
                                                                domain = null,
                                                                subType = null,
                                                                subTypeLabel = null,
                                                                projectName = null }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [internalActiveCode, setInternalActiveCode] = useState(null);
  const activeCode = externalActiveCode ?? internalActiveCode;
  const setActiveCode = (c) => {
    if (onActiveCodeChange) onActiveCodeChange(c);
    setInternalActiveCode(c);
  };

  // Phase 19n — when the user clicks "Back to Stage X" from a tab that was
  // opened via a cross-link, switch to that stage on mount.
  useEffect(() => {
    try {
      const want = sessionStorage.getItem("rachana.stage.activate");
      if (want) {
        setActiveCode(want);
        sessionStorage.removeItem("rachana.stage.activate");
      }
    } catch {}
  }, []);

  // Wave V.5 — live auto-navigation when the approve endpoint unblocks a
  // downstream L2 stage and the user clicks "Open X — Name".
  useEffect(() => {
    const onActivate = (e) => {
      const code = e?.detail?.code;
      if (code) setActiveCode(code);
    };
    window.addEventListener("rachana.stage.activate", onActivate);
    return () => window.removeEventListener("rachana.stage.activate", onActivate);
  }, []);
  const [err, setErr] = useState("");
  const [routerPrompt, setRouterPrompt] = useState("");
  const [routing, setRouting] = useState(false);
  const [routedTo, setRoutedTo] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Memoised so StagePanel's useEffect dependency array doesn't trip on every
  // parent re-render (which previously spawned orphan WebSocket reconnects).
  const handleStageChange = useCallback((newCode) => {
    setRefreshTick((t) => t + 1);
    // Wave CG.3 — when ParentStageRollup → "open sub-stage" is clicked,
    // the callback fires with the sub-stage code. Navigate to it.
    if (typeof newCode === "string" && newCode.length > 0
        && /^[A-Z]\.\d/.test(newCode)) {
      setActiveCode(newCode);
    }
  }, []);

  const reload = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/stages`);
      setData(data);
      setErr("");
      if (!activeCode && data.stages?.length) {
        // First incomplete leaf stage, else first stage
        const next = data.stages.find((s) => s.is_leaf && s.status === "not_started")
                  || data.stages.find((s) => s.is_leaf)
                  || data.stages[0];
        setActiveCode(next?.code || data.stages[0].code);
      }
    } catch (e) {
      // Treat 502/503/504 as transient — keep prior data on screen and
      // let the auto-poll re-attempt. Only surface the error on hard
      // failures (4xx with detail, or no response at all).
      const code = e.response?.status;
      if (code === 502 || code === 503 || code === 504) {
        // Backend is busy with a pipeline call — quietly retry on next tick
        if (!data) setErr("Backend is busy generating designs. Reloading…");
        return;
      }
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId, refreshTick]);

  // File 47 dynamic stage gate (so we can resolve names for L2 codes like
  // B.1.2 that the legacy /stages endpoint doesn't include).
  const dynamicGate = useStageGate(projectId);
  const dynamicCatalogue = dynamicGate?.data?.catalogue || [];

  // Retry every 5s while the initial load hasn't succeeded yet (e.g. backend
  // is temporarily 502'ing because a pipeline call is occupying it).
  useEffect(() => {
    if (data) return;
    const id = setInterval(() => setRefreshTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [data]);

  // Auto-poll while any stage is in_progress
  useEffect(() => {
    if (!data) return;
    const anyInProgress = data.stages.some((s) => s.status === "in_progress");
    if (!anyInProgress) return;
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [data]); // eslint-disable-line

  const toggleApprovalGates = async () => {
    try {
      await api.patch(`/projects/${projectId}/approval-gates`, { enabled: !data.approval_gates_enabled });
      setRefreshTick((t) => t + 1);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  const sendFreePrompt = async (promptOverride) => {
    const used = (promptOverride ?? routerPrompt).trim();
    if (!used) return;
    setRouting(true); setRoutedTo(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/prompt`, { prompt: used });
      setRoutedTo(data.routed_to);
      setActiveCode(data.routed_to);
      setRouterPrompt("");
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRouting(false); }
  };

  if (err) return <div className="pf-sev pf-sev-error" style={{ display: "block", padding: 12 }}>{err}</div>;
  if (!data) return <div style={{ padding: 24, color: "var(--pf-text-muted)" }}>Loading stage gate…</div>;

  const flatByCode = Object.fromEntries(data.stages.map((s) => [s.code, s]));
  const top = (data.catalogue || []).filter((s) => !s.code.includes("."));

  // For dynamic infra/RE stage codes (B.1.2, D.1.1, etc.), the legacy /stages
  // endpoint returns only L1 parents — synthesize an activeStage from the
  // dynamic File 47 catalogue if the code isn't in the legacy flat list.
  let activeStage = flatByCode[activeCode];
  if (!activeStage && activeCode) {
    const cat = (data.catalogue || []).find((c) => c.code === activeCode)
                  || dynamicCatalogue.find((c) => c.code === activeCode);
    if (cat) {
      activeStage = {
        code: cat.code,
        name: cat.name,
        status: "not_started",
        deliverables: [],
        sub_stages: [],
        required: cat.required !== false,
        // Propagate project-level routing context so InfraStageDynamicForm
        // can resolve the per-sub-sector 3-level schema dispatcher.
        sub_type: cat.sub_type || subType || null,
        industry: cat.industry || null,
        pattern: cat.pattern || null,
      };
    }
  }
  if (!activeStage) activeStage = data.stages[0];
  // Even when activeStage came from the legacy flat list, ensure it carries
  // the project-level sub_type so the dispatcher routing always works.
  if (activeStage && !activeStage.sub_type && subType) {
    activeStage = { ...activeStage, sub_type: subType };
  }

  return (
    <div data-testid="stage-workspace"
        style={ hideRail
          ? { display: "flex", flexDirection: "column", height: "100%" }
          : { display: "grid", gridTemplateColumns: "320px 1fr", height: "100%", gap: 0 } }>
      {!hideRail && (
      <div style={{ borderRight: "1px solid var(--pf-border)", overflow: "auto", display: "flex", flexDirection: "column", background: "var(--pf-surface)" }}>
        <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid var(--pf-border)", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ListChecks size={16} color="var(--pf-spark)" />
            <span className="pf-overline" style={{ margin: 0 }}>STAGE GATE · {data.domain.replace(/_/g, " ").toUpperCase()}</span>
          </div>
          {subTypeLabel && subTypeLabel !== "—" && (
            <div data-testid="stage-workspace-subtype"
                 title={subType || ""}
                 style={{ fontSize: 12, color: "var(--pf-spark)",
                          fontWeight: 600, lineHeight: 1.35,
                          paddingLeft: 24, wordBreak: "break-word" }}>
              {subTypeLabel}
            </div>
          )}
          {projectName && (
            <div data-testid="stage-workspace-project-name"
                 style={{ fontSize: 11, color: "var(--pf-text-muted)",
                          paddingLeft: 24, lineHeight: 1.3,
                          overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap" }}>
              {projectName}
            </div>
          )}
        </div>

        {/* DPR download — visible only for infrastructure projects */}
        {domain === "infrastructure" && (
          <div style={{ padding: "8px 14px", borderBottom: "1px solid var(--pf-border)" }}>
            <DownloadDPRButton projectId={projectId} label="Download DPR (PDF)" />
          </div>
        )}

        {/* Approval gates toggle */}
        <button
          type="button"
          onClick={toggleApprovalGates}
          style={{ background: "transparent", border: "none", padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid var(--pf-border)", display: "flex", alignItems: "center", gap: 10, color: "var(--pf-text)" }}
          data-testid="approval-gates-toggle"
        >
          <span style={{ width: 28, height: 14, borderRadius: 7, background: data.approval_gates_enabled ? "var(--pf-accent)" : "var(--pf-border)", position: "relative", transition: "background .2s" }}>
            <span style={{ position: "absolute", top: 1, left: data.approval_gates_enabled ? 15 : 1, width: 12, height: 12, borderRadius: "50%", background: "white", transition: "left .2s" }} />
          </span>
          <span style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>Approval gates {data.approval_gates_enabled ? "ON" : "OFF"}</span>
        </button>

        {/* Free-form prompt router */}
        <div style={{ padding: 12, borderBottom: "1px solid var(--pf-border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div className="pf-overline">{t("ask_anything", "ASK ANYTHING")}</div>
            <MicButton testId="free-prompt-mic" size="sm"
                onTranscript={(t2) => setRouterPrompt(prev => prev ? prev + " " + t2 : t2)}
                onAutoSubmit={(t2) => { setRouterPrompt(t2); sendFreePrompt(t2); }} />
          </div>
          <textarea
            className="pf-input pf-textarea"
            rows={2}
            placeholder="e.g. resize the column for 99.5% benzene · hold Space to dictate"
            value={routerPrompt}
            onChange={(e) => setRouterPrompt(e.target.value)}
            data-testid="free-prompt-input"
            style={{ fontSize: 12, padding: 8 }}
          />
          <button className="btn btn-ghost" style={{ marginTop: 6, width: "100%" }} disabled={routing || !routerPrompt.trim()} onClick={() => sendFreePrompt()} data-testid="free-prompt-submit">
            <Sparkles size={13} /> {routing ? "Routing…" : t("send", "Send")}
          </button>
          {routedTo && <div style={{ marginTop: 6, fontSize: 11, color: "var(--pf-spark)", fontFamily: "'JetBrains Mono', monospace" }}>Routed → Stage {routedTo}</div>}
        </div>

        {/* Stage list */}
        <div style={{ flex: 1, paddingBottom: 16 }}>
          {top.map((s) => (
            <StageRailItem
              key={s.code}
              stage={s}
              flatByCode={flatByCode}
              activeCode={activeCode}
              onPick={setActiveCode}
            />
          ))}
        </div>
      </div>
      )}

      {/* RIGHT PANEL */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden",
                          display: "flex", flexDirection: "column" }}>
        {activeStage && (() => {
          // For infra/RE projects, use the inline InfraStagePanel which renders
          // the matching editor (Alignment, Bridges, Drainage, etc.) for codes
          // that have an editor mapping. Codes that map to null fall back to a
          // deliverable preview within InfraStagePanel.
          const isInfraOrRE = (domain === "infrastructure"
                                  || domain === "real_estate");
          if (isInfraOrRE) {
            return (
              <InfraStagePanel
                key={activeStage.code}
                projectId={projectId}
                stage={activeStage}
                domain={domain}
                onChange={handleStageChange}
              />
            );
          }
          return (
            <StagePanel
              key={activeStage.code}
              projectId={projectId}
              stage={activeStage}
              domain={domain}
              approvalGatesEnabled={data.approval_gates_enabled}
              onChange={handleStageChange}
              onOpenTab={onOpenTab}
              onSendHandover={onSendHandover}
              extraTabs={stageExtraTabs}
            />
          );
        })()}
      </div>
    </div>
  );
}

function StageRailItem({ stage, flatByCode, activeCode, onPick }) {
  const [open, setOpen] = useState(true);
  const top_state = flatByCode[stage.code] || { status: "not_started" };
  const meta = STATUS_META[top_state.status] || STATUS_META.not_started;
  const Icon = meta.Icon;
  const subs = (stage.sub_stages || []).map((ss) => flatByCode[ss.code]).filter(Boolean);
  const isActive = activeCode === stage.code;
  const hasSubs = subs.length > 0;

  return (
    <div data-testid={`rail-stage-${stage.code}`}>
      <button
        type="button"
        onClick={() => { onPick(stage.code); if (hasSubs) setOpen(!open); }}
        style={{
          width: "100%", textAlign: "left", border: "none",
          background: isActive ? "var(--pf-surface-2)" : "transparent",
          padding: "8px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
          color: "var(--pf-text)", borderLeft: isActive ? "2px solid var(--pf-spark)" : "2px solid transparent",
        }}
      >
        {hasSubs ? <ChevronRight size={12} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s", color: "var(--pf-text-dim)" }} /> : <span style={{ width: 12 }} />}
        <Icon size={13} color={meta.color} />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-spark)", minWidth: 28 }}>{stage.code}</span>
        <span style={{ fontSize: 13, flex: 1, color: isActive ? "var(--pf-text)" : "var(--pf-text-muted)" }}>{stage.name}</span>
      </button>
      {hasSubs && open && (
        <div style={{ paddingLeft: 18 }}>
          {subs.map((ss) => {
            const sm = STATUS_META[ss.status] || STATUS_META.not_started;
            const SIcon = sm.Icon;
            const sActive = activeCode === ss.code;
            return (
              <button
                key={ss.code}
                type="button"
                onClick={() => onPick(ss.code)}
                data-testid={`rail-substage-${ss.code}`}
                style={{
                  width: "100%", textAlign: "left", border: "none",
                  background: sActive ? "var(--pf-surface-2)" : "transparent",
                  padding: "6px 14px 6px 28px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                  color: "var(--pf-text)", borderLeft: sActive ? "2px solid var(--pf-spark)" : "2px solid transparent",
                }}
              >
                <SIcon size={11} color={sm.color} />
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--pf-spark)", minWidth: 32 }}>{ss.code}</span>
                <span style={{ fontSize: 12, flex: 1, color: sActive ? "var(--pf-text)" : "var(--pf-text-muted)" }}>{ss.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
